import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import dayjs from 'dayjs';
import clientInboxApi from '@src/api/clientInbox';

// "Ask the drafter": a staff conversation with the thread agent
// (POST/GET /client-inbox/email-messages/<id>/agent-turns/). The agent can
// look things up, answer, and rewrite the thread's reply draft.

const IN_FLIGHT = new Set(['queued', 'running']);
const POLL_MS = 3000;
export const INVESTIGATE_MESSAGE = 'Investigate this thread and draft the reply.';
const INVESTIGATE_TOOLTIP = 'Digs deeper: queries any table, reads our code. Can take several minutes.';

function LookupList({ calls }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(calls) || calls.length === 0) return null;
  return (
    <Box mt={0.5}>
      <Button
        size="small"
        variant="text"
        color="inherit"
        onClick={() => setOpen((v) => !v)}
        endIcon={
          <ExpandMoreIcon
            sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          />
        }
        sx={{ textTransform: 'none', fontSize: 12, px: 0.5, minWidth: 0, color: 'text.secondary' }}
      >
        {calls.length} lookup{calls.length === 1 ? '' : 's'}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.75} mt={0.5}>
          {calls.map((call, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={idx}>
              <Typography variant="caption" component="div">
                <strong>{call.name || 'tool'}</strong>
                {call.purpose ? ` — ${call.purpose}` : ''}
              </Typography>
              {call.sql ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.25,
                    p: 0.75,
                    fontFamily: 'monospace',
                    fontSize: 11,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {call.sql}
                </Box>
              ) : null}
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

LookupList.propTypes = { calls: PropTypes.array };
LookupList.defaultProps = { calls: [] };

function AgentTurn({ turn, onUseDraft }) {
  const isStaff = turn.role === 'user';
  const inFlight = IN_FLIGHT.has(turn.status);
  const investigate = turn.mode === 'investigate';

  let body;
  if (!isStaff && inFlight) {
    body = (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={12} />
        <Typography variant="body2" color="text.secondary">
          {investigate ? 'Investigating… this can take a few minutes' : 'Working…'}
        </Typography>
      </Stack>
    );
  } else if (!isStaff && turn.status === 'error') {
    body = (
      <Typography variant="body2" color="error.main" sx={{ whiteSpace: 'pre-wrap' }}>
        {turn.error || turn.content || 'Something went wrong on this one.'}
      </Typography>
    );
  } else if (!isStaff && turn.status === 'stale') {
    body = (
      <Typography variant="body2" color="text.secondary">
        This one stopped without an answer. Send it again.
      </Typography>
    );
  } else {
    // Plain text with newlines; rendered as a text node, never as HTML.
    body = (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {turn.content}
      </Typography>
    );
  }

  return (
    <Box display="flex" justifyContent={isStaff ? 'flex-end' : 'flex-start'}>
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: isStaff ? 'primary.50' : 'grey.100',
          border: '1px solid',
          borderColor: isStaff ? 'primary.100' : 'divider',
        }}
      >
        {body}
        {!isStaff && turn.status === 'done' && turn.draft_updated ? (
          <Chip label="Draft updated" size="small" color="success" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
        ) : null}
        {!isStaff && turn.status === 'done' && turn.draft_held ? (
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="warning.dark">
              A regenerate was running, so the draft wasn&apos;t replaced
            </Typography>
            {turn.reply_draft ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onUseDraft(turn)}
                sx={{ textTransform: 'none', fontSize: 12, py: 0, minHeight: 0 }}
              >
                Use this draft
              </Button>
            ) : null}
          </Stack>
        ) : null}
        {!isStaff ? <LookupList calls={turn.tool_calls} /> : null}
        {turn.created_at ? (
          <Typography variant="caption" color="text.disabled" component="div" textAlign={isStaff ? 'right' : 'left'}>
            {dayjs(turn.created_at).format('h:mm A')}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

AgentTurn.propTypes = {
  turn: PropTypes.object.isRequired,
  onUseDraft: PropTypes.func.isRequired,
};

/**
 * Props:
 *   threadId        the open thread. Every async result is dropped if the
 *                   thread changed while it was in flight.
 *   onDraftUpdated  (threadId, turn) — a turn finished and rewrote the
 *                   thread's reply draft; the parent reloads the thread.
 *   onUseDraft      (threadId, html) — staff chose a held draft.
 * Ref: { investigate(message?) } posts an investigate turn and scrolls here.
 */
const ThreadAgentPanel = forwardRef(function ThreadAgentPanel({ threadId, onDraftUpdated, onUseDraft }, ref) {
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [forbidden, setForbidden] = useState(false);

  const threadRef = useRef(threadId);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  // Assistant turns seen queued/running in this session. Only those trigger a
  // draft refresh when they finish — old history never does.
  const watchedRef = useRef(new Set());
  const appliedRef = useRef(new Set());
  const onDraftUpdatedRef = useRef(onDraftUpdated);
  onDraftUpdatedRef.current = onDraftUpdated;

  const applyTurns = useCallback((tid, list) => {
    if (threadRef.current !== tid) return;
    const rows = Array.isArray(list) ? list : [];
    rows.forEach((turn) => {
      if (turn.role !== 'assistant') return;
      if (IN_FLIGHT.has(turn.status)) {
        watchedRef.current.add(turn.id);
      } else if (
        turn.status === 'done' &&
        turn.draft_updated &&
        watchedRef.current.has(turn.id) &&
        !appliedRef.current.has(turn.id)
      ) {
        appliedRef.current.add(turn.id);
        if (onDraftUpdatedRef.current) onDraftUpdatedRef.current(tid, turn);
      }
    });
    setTurns(rows);
  }, []);

  // Load history whenever the thread changes; reset everything first.
  useEffect(() => {
    threadRef.current = threadId;
    watchedRef.current = new Set();
    appliedRef.current = new Set();
    setTurns([]);
    setInput('');
    setNotice('');
    setSending(false);
    setForbidden(false);
    if (!threadId) return undefined;
    const tid = threadId;
    setLoading(true);
    clientInboxApi
      .listThreadAgentTurns(tid)
      .then((res) => applyTurns(tid, res?.data?.results))
      .catch((err) => {
        if (threadRef.current !== tid) return;
        if (err?.response?.status === 403) setForbidden(true);
      })
      .finally(() => {
        if (threadRef.current === tid) setLoading(false);
      });
    return undefined;
  }, [applyTurns, threadId]);

  const latestAssistant = [...turns].reverse().find((t) => t.role === 'assistant') || null;
  const inFlight = Boolean(latestAssistant && IN_FLIGHT.has(latestAssistant.status));

  // Poll while the latest assistant turn is queued/running. The effect's
  // cleanup stops it on thread change, unmount, or once the turn settles.
  useEffect(() => {
    if (!threadId || !inFlight) return undefined;
    const tid = threadId;
    let busy = false;
    const timer = setInterval(() => {
      if (busy) return;
      busy = true;
      clientInboxApi
        .listThreadAgentTurns(tid)
        .then((res) => applyTurns(tid, res?.data?.results))
        .catch(() => {})
        .finally(() => {
          busy = false;
        });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [applyTurns, inFlight, threadId, latestAssistant?.id]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, latestAssistant?.status]);

  const send = useCallback(
    (text, mode) => {
      const tid = threadRef.current;
      const messageText = (text || '').trim();
      if (!tid || !messageText || sending) return;
      setSending(true);
      setNotice('');
      clientInboxApi
        .createThreadAgentTurn(tid, { message: messageText, mode })
        .then((res) => {
          if (threadRef.current !== tid) return;
          const { user_turn: userTurn, assistant_turn: assistantTurn } = res?.data || {};
          if (assistantTurn?.id) watchedRef.current.add(assistantTurn.id);
          setTurns((prev) => [...prev, userTurn, assistantTurn].filter(Boolean));
          setInput('');
        })
        .catch((err) => {
          if (threadRef.current !== tid) return;
          const code = err?.response?.status;
          if (code === 409) {
            setNotice('Still working on the last message');
            clientInboxApi
              .listThreadAgentTurns(tid)
              .then((res) => applyTurns(tid, res?.data?.results))
              .catch(() => {});
          } else if (code === 400) {
            setNotice(err?.response?.data?.detail || 'Type a message first.');
          } else {
            setNotice(err?.response?.data?.detail || 'Could not send that. Try again.');
          }
        })
        .finally(() => {
          if (threadRef.current === tid) setSending(false);
        });
    },
    [applyTurns, sending],
  );

  useImperativeHandle(
    ref,
    () => ({
      investigate: (text) => {
        if (rootRef.current?.scrollIntoView) {
          rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        send(text || INVESTIGATE_MESSAGE, 'investigate');
      },
    }),
    [send],
  );

  const handleUseDraft = useCallback(
    (turn) => {
      const tid = threadRef.current;
      if (!tid || !turn?.reply_draft || !onUseDraft) return;
      onUseDraft(tid, turn.reply_draft);
    },
    [onUseDraft],
  );

  if (!threadId || forbidden) return null;

  const busy = sending || inFlight;

  return (
    <Box ref={rootRef} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        Ask the drafter
      </Typography>
      {loading && turns.length === 0 ? (
        <Box py={1}>
          <CircularProgress size={14} />
        </Box>
      ) : null}
      {turns.length > 0 ? (
        <Stack ref={listRef} spacing={0.75} sx={{ maxHeight: 360, overflowY: 'auto', my: 1, pr: 0.5 }}>
          {turns.map((turn) => (
            <AgentTurn key={turn.id || turn.uid} turn={turn} onUseDraft={handleUseDraft} />
          ))}
        </Stack>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="flex-end" mt={turns.length ? 0 : 0.5}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          placeholder="Ask about this thread or tell the drafter what to change…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
              e.preventDefault();
              send(input, 'draft');
            }
          }}
          InputProps={{ sx: { fontSize: '0.85rem' } }}
        />
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={() => send(input, 'draft')}
          disabled={busy || !input.trim()}
          startIcon={<SendIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Send
        </Button>
        <Tooltip title={INVESTIGATE_TOOLTIP}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => send(input.trim() || INVESTIGATE_MESSAGE, 'investigate')}
              disabled={busy}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Investigate
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {notice ? (
        <Typography variant="caption" color="warning.dark" component="div" mt={0.5}>
          {notice}
        </Typography>
      ) : null}
    </Box>
  );
});

ThreadAgentPanel.propTypes = {
  threadId: PropTypes.number,
  onDraftUpdated: PropTypes.func,
  onUseDraft: PropTypes.func,
};

ThreadAgentPanel.defaultProps = {
  threadId: null,
  onDraftUpdated: undefined,
  onUseDraft: undefined,
};

export default ThreadAgentPanel;
